import { Injectable } from "@angular/core";
import { AuthService, UserService, GameTrackerService, HealthService, GameTrackerPublicService, ScoutingService, CustomEliteConfigurationService, BillingService, SuperadminService, AnalyticsService, IntegrationsService } from "./api/services";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { HttpContext } from "@angular/common/http";
import { CUSTOM_REQUEST_AUTHORIZATION, CUSTOM_REQUEST_TIMEOUT_TOKEN } from "./auth/auth.interceptor";
import { BillingStatusDto } from "./api/models";
import { CoreProvider } from "./core.provider";

@Injectable({
    providedIn: "root",
})
export class ApiProvider {

    private connectedToCloudInterval: any;
    private connectedToCloudContext = new HttpContext();
    connectedToCloud$ = new BehaviorSubject<boolean>(false);

    private checkSubscriptionStatusInterval: any;
    private checkSubscriptionStatusIntervalStartTime?: number;
    billingStatus$ = new BehaviorSubject<BillingStatusDto>({isActive: false, status: 'inactive', freeGamesRemaining: 3}); // Intentionally set 3 instead of 5 to create 2 games margin in case of failed billing status check
    get isUpgradeRequired() {
        const localOnlyGames = this.core.ldGamesService.games.filter(game => !game.uuid).length;
        return !['active','free'].includes(this.billingStatus$.value.status) && this.billingStatus$.value.freeGamesRemaining <= (0 + localOnlyGames);
    }

    private core!: CoreProvider;
    setCore(core: CoreProvider) {
        this.core = core;

        // Load last billing status from storage
        this.core.storage.getItem('last-billing-status').then(async (lastBillingStatus) => {
            if (lastBillingStatus) this.billingStatus$.next(JSON.parse(lastBillingStatus) as BillingStatusDto);
        });
    }

    constructor(
        public readonly health: HealthService,
        public readonly auth: AuthService,
        public readonly user: UserService,
        public readonly billing: BillingService,
        public readonly scouting: ScoutingService,
        public readonly gameTracker: GameTrackerService,
        public readonly gameTrackerPublic: GameTrackerPublicService,
        public readonly customEliteConfigurationService: CustomEliteConfigurationService,
        public readonly superadmin: SuperadminService,
        public readonly analytics: AnalyticsService,
        public readonly integrations: IntegrationsService,
    ) {
        this.connectedToCloudContext.set(CUSTOM_REQUEST_AUTHORIZATION, 'no-auth-required'); // Prevent waiting for tokens initialization
        this.connectedToCloudContext.set(CUSTOM_REQUEST_TIMEOUT_TOKEN, 2000);
        const checkConnection = async () => {
            try {
                await firstValueFrom(this.health.healthControllerPing({}, this.connectedToCloudContext));
                if (this.connectedToCloud$.value !== true) {
                    this.connectedToCloud$.next(true);
                    if (this.core.auth.isAuthenticated) {
                        this.checkSubscriptionStatus();
                    }
                }
            } catch (error) {
                if (this.connectedToCloud$.value !== false) {
                    this.connectedToCloud$.next(false);
                }
            }
        };
        if (this.connectedToCloudInterval) clearInterval(this.connectedToCloudInterval);
        this.connectedToCloudInterval = setInterval(checkConnection, 4000);
        checkConnection();
    }

    async checkSubscriptionStatus(): Promise<BillingStatusDto> {
        try {
            const subscriptionStatus = await firstValueFrom(this.billing.billingControllerGetBillingStatus({})) as BillingStatusDto;
            this.core.storage.setItem('last-billing-status', JSON.stringify(subscriptionStatus));
            this.billingStatus$.next(subscriptionStatus);
            if (subscriptionStatus.isActive && this.checkSubscriptionStatusInterval) {
                clearInterval(this.checkSubscriptionStatusInterval);
                this.checkSubscriptionStatusInterval = undefined;
            }
            return subscriptionStatus;
        } catch (error) {
            console.error('Error checking subscription status', error);
            throw error;
        }
    }

    async afterPurchaseStartCheckInterval(): Promise<void> {
        this.checkSubscriptionStatusIntervalStartTime = Date.now();
        this.checkSubscriptionStatusInterval = setInterval(async () => {
            await this.checkSubscriptionStatus();
            if (
                this.checkSubscriptionStatusInterval &&
                this.checkSubscriptionStatusIntervalStartTime &&
                Date.now() - this.checkSubscriptionStatusIntervalStartTime! > 15 * 60 * 1000 // 15 minutes to avoid infinite loop
            ) {
                clearInterval(this.checkSubscriptionStatusInterval);
                this.checkSubscriptionStatusInterval = undefined;
            }
        }, 30000);
    }
}
